import { ProductReviewsResponse, ReviewDataForSearch } from '@/types'
import { Wrapper } from './ProductReviews.styles'
import {
  Spacing,
  Heading,
  ReviewCard,
  ReviewGallery,
  ReviewSummary,
  GalleryOverlay,
} from '@big-red-group/storefront-common-ui'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const ProductReviews: React.FC<ProductReviewsResponse> = ({ average, total, loadedReviews, reviews }) => {
  const [showPopup, setShowPopup] = useState(false)
  const [currentImage, setCurrentImage] = useState<number>(0)
  const images = reviews.flatMap((review) => review.attachments?.filter((attachment) => attachment !== null) || [])
  const [shownImages, setShownImages] = useState<string[]>(images)
  const handleCarouselImageClicks = (index: number) => {
    setCurrentImage(index)
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowPopup(false)
        return
      }

      if (event.key === 'ArrowLeft') {
        setCurrentImage(currentImage === 0 ? shownImages!.length - 1 : currentImage! - 1)
        return
      }

      if (event.key === 'ArrowRight') {
        setCurrentImage(currentImage === shownImages!.length - 1 ? 0 : currentImage! + 1)
        return
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [setCurrentImage, currentImage, shownImages])

  const showAllImages = (i: number) => {
    setShowPopup(true)
    setShownImages(images)
    setCurrentImage(i)
  }

  return (
    <>
      <Wrapper id="product-reviews">
        <Heading size="L" dataTestId="product-reviews-heading">
          Recent reviews
        </Heading>
        <ReviewSummary average={average} total={total} />
        <Spacing size="M" />
        <ReviewGallery images={images} onImageClick={showAllImages} />
        {loadedReviews?.map(({ id, author, date, rating, title, blurb, summary, attachments }, index) => (
          <div key={index} data-testid={`review-${index}`}>
            <ReviewCard
              id={id}
              author={author}
              date={date}
              rating={rating}
              title={title}
              summary={summary}
              blurb={blurb}
              attachments={attachments}
              onImageClick={(images, i) => {
                setShowPopup(true)
                setShownImages(images)
                setCurrentImage(i)
              }}
            />
            {index + 1 !== reviews.length && <Spacing size="M" />}
          </div>
        ))}
      </Wrapper>
      <Spacing size="S" />
      {showPopup &&
        createPortal(
          <GalleryOverlay
            images={shownImages}
            currentImage={currentImage}
            onClose={() => setShowPopup(false)}
            onPrev={() => handleCarouselImageClicks(currentImage === 0 ? shownImages.length - 1 : currentImage - 1)}
            onNext={() => handleCarouselImageClicks(currentImage === shownImages.length - 1 ? 0 : currentImage + 1)}
          />,
          document.body
        )}
    </>
  )
}

export default ProductReviews
